/** Shared Analytics / dashboard / reports query param mapping to API contract. */

/** FE: all|pos|delivery → API: ALL|POS|DELIVERY */
export function toApiChannel(channel) {
  const key = String(channel || 'all').toLowerCase();
  if (key === 'pos') return 'POS';
  if (key === 'delivery') return 'DELIVERY';
  return 'ALL';
}

/** FE UI period keys → API period enum (this_month → month). */
export function toApiPeriod(period) {
  if (period === 'this_month') return 'month';
  return period || undefined;
}

/** FE boolean → API compare=1 when on; omit when off */
export function toApiCompare(compare) {
  return compare ? '1' : undefined;
}

/**
 * Common dashboard query fields.
 * period wins: from/to are only sent when period=custom (BE ignores them on presets).
 * Omitting period defaults to 7d on the API.
 */
export function toDashboardQuery({ from, to, channel, compare, period, ...rest }) {
  const apiPeriod = toApiPeriod(period);
  const q = {
    channel: toApiChannel(channel),
    period: apiPeriod,
    ...rest,
  };
  if (apiPeriod === 'custom') {
    q.from = from;
    q.to = to;
  }
  if (compare !== undefined) {
    q.compare = toApiCompare(compare);
  }
  return q;
}

/** Bucket label from API `t` (hour = YYYY-MM-DDTHH; day/week = YYYY-MM-DD). */
export function formatBucketLabel(rawT) {
  if (typeof rawT !== 'string') return String(rawT ?? '');
  // Hour: 2026-09-04T14
  if (/^\d{4}-\d{2}-\d{2}T\d{2}$/.test(rawT)) {
    return `${rawT.slice(11, 13)}:00`;
  }
  // Day / week Monday: 2026-09-04
  if (/^\d{4}-\d{2}-\d{2}$/.test(rawT)) {
    return rawT.slice(5);
  }
  return rawT;
}

/** Map analytics API error codes to user-facing copy. */
export function mapAnalyticsError(err, { maxReportDays = 366 } = {}) {
  const code = err?.code;
  switch (code) {
    case 'range_too_long':
      return `That date range is too long (max ${err.max_days ?? maxReportDays} days).`;
    case 'invalid_granularity':
      return 'Hour view is only available for ranges of 2 days or less.';
    case 'invalid_period':
      return 'Invalid time period.';
    case 'invalid_range':
    case 'invalid_date':
      return 'Invalid date range.';
    case 'from_and_to_required':
      return 'Custom range requires both from and to dates.';
    case 'invalid_channel':
      return 'Invalid channel filter.';
    case 'invalid_metric':
      return 'Invalid metric.';
    case 'invalid_limit':
      return 'Invalid limit.';
    case 'invalid_type':
      return 'Invalid report type.';
    case 'invalid_format':
      return 'Invalid report format.';
    case 'forbidden':
      return 'You do not have permission to do this.';
    case 'not_found':
      return 'Not found.';
    case 'not_ready':
      return 'Report is not ready yet. Try again in a moment.';
    case 'sales_bucket_missing':
      return 'Analytics data is temporarily unavailable. Try again later.';
    default:
      if (err?.status === 401) return 'Sign in again to continue.';
      if (err?.status === 403) return 'You do not have permission to do this.';
      return err?.detail || err?.message || 'Request failed.';
  }
}
